import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Circle,
  User,
  Building2,
  Database,
  Mail,
  ArrowRight,
  PartyPopper,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  organizationApi,
  type SetupStep,
  type SetupProgressData,
} from '@/services/organization-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const STEP_ICONS: Record<string, LucideIcon> = {
  organization: Building2,
  profile: User,
  sources: Database,
  outreach: Mail,
}

const STEP_ACTIONS: Record<string, { label: string; path: string }> = {
  organization: { label: 'Create Org', path: '/app/settings/organization' },
  profile: { label: 'Edit Profile', path: '/app/settings/account' },
  sources: { label: 'Add Source', path: '/app/sources' },
  outreach: { label: 'Configure', path: '/app/outreach' },
}

const DISMISSED_KEY = 'setup-progress-dismissed'

function SetupProgressSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-4 w-8" />
        </div>
        <Skeleton className="mt-2 h-2 w-full rounded-full" />
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <li key={i} className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-3">
              <Skeleton className="mt-0.5 h-5 w-5 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-48" />
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function SetupCompleteCard({ onDismiss }: { onDismiss: () => void }) {
  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <PartyPopper className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base text-card-foreground">
                Setup Complete
              </CardTitle>
              <CardDescription>
                You're all set! Your workspace is ready to use.
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={onDismiss}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        </div>
        <Progress value={100} className="mt-3" />
      </CardHeader>
    </Card>
  )
}

export default function SetupProgress() {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  const { data, isLoading } = useQuery<SetupProgressData>({
    queryKey: ['setup-progress'],
    queryFn: organizationApi.getSetupProgress,
    staleTime: 1000 * 60,
  })

  const handleDismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISSED_KEY, 'true')
    } catch {
      // Ignore localStorage errors
    }
  }

  if (isLoading) {
    return <SetupProgressSkeleton />
  }

  if (!data) {
    return null
  }

  if (data.all_done) {
    if (dismissed) {
      return null
    }
    return <SetupCompleteCard onDismiss={handleDismiss} />
  }

  const { steps, completed_count, total_count, percentage } = data

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Setup Progress</CardTitle>
            <CardDescription>
              {completed_count} of {total_count} steps completed
            </CardDescription>
          </div>
          <span className="text-sm font-semibold tabular-nums text-primary">
            {percentage}%
          </span>
        </div>
        <Progress value={percentage} className="mt-2 h-2" />
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-2">
          {steps.map((step: SetupStep) => {
            const Icon = STEP_ICONS[step.id] || Circle
            const action = STEP_ACTIONS[step.id]

            return (
              <li
                key={step.id}
                className={cn(
                  'flex items-start gap-3 rounded-lg p-3 transition-colors',
                  step.completed
                    ? 'bg-muted/40'
                    : 'border border-border bg-card'
                )}
              >
                <div className="mt-0.5">
                  {step.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  ) : (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-muted-foreground/30">
                      <Icon className="h-3 w-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      step.completed
                        ? 'text-muted-foreground line-through decoration-muted-foreground/50'
                        : 'text-card-foreground'
                    )}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {step.description}
                  </p>
                </div>
                {action && !step.completed && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => navigate(action.path)}
                  >
                    {action.label}
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
